import { DashboardOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, MenuProps } from 'antd';
import { useCallback } from 'react';
import { Link } from 'react-router';

import sharedStyles from '../../../side-nav/SideNav.shared.module.css';

import { useSharedState } from '../../../hooks/use-shared-state';
import { DASHBOARDS_ITEM_KEY, DASHBOARDS_ITEM_KEY_PREFIX, searchableMenuItemLabelMap } from '../../../side-nav/sidenav-constants';
import { DashboardData } from '../dashboard-api';
import { dashboardListState, initialDashboardCreate } from '../models/dashboard-list';
import { DashboardRemoveDialog } from './DashboardRemoveDialog';
import { DashboardUpsertDialog } from './DashboardUpsertDialog';

function DashboardNavItem({ model }: { model: DashboardData }) {
  const [, setDashboardList] = useSharedState(dashboardListState);

  const onRemove = () => {
    setDashboardList({ ...dashboardListState.get(), remove: { dashboardId: model.id } });
  };

  return (
    <div className={sharedStyles.navLabel}>
      <Link to={`/dashboard/${model.id}`}>{model.name}</Link>
      <Button icon={<DeleteOutlined />} type="text" danger onClick={onRemove} data-testid={`delete-dashboard-button-${model.id}`} />
    </div>
  );
}

function DashboardNavMainLabel() {
  const stopPropagation = useCallback((evt: React.SyntheticEvent) => evt.stopPropagation(), []);

  const [model, setDashboardList] = useSharedState(dashboardListState);

  const onCreate = () => {
    setDashboardList({ ...dashboardListState.get(), upsert: initialDashboardCreate() });
  };

  return (
    <>
      <div data-testid="side-nav-dashboards" className="sidebar-nav-item-title-icon-container">
        <div>{searchableMenuItemLabelMap.DASHBOARDS}</div>
        <Button icon={<PlusOutlined />} type="text" onClick={onCreate} />
      </div>

      {/* See https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/3d1d26d89d492947cbf69f439deec9e7cfaf9867/docs/rules/no-static-element-interactions.md#case-the-event-handler-is-only-being-used-to-capture-bubbled-events */}
      {/*eslint-disable-next-line jsx-a11y/no-static-element-interactions*/}
      <div onKeyDown={stopPropagation} onClick={stopPropagation}>
        {model.upsert && <DashboardUpsertDialog model={model.upsert} />}
        {model.remove && <DashboardRemoveDialog model={model.remove} />}
      </div>
    </>
  );
}

function getDashboardNavItems({ dashboards, isLoading }: { dashboards: DashboardData[]; isLoading: boolean }) {
  return dashboards.length > 0
    ? dashboards.map((d) => ({
        icon: <DashboardOutlined />,
        label: <DashboardNavItem model={d} />,
        key: `${DASHBOARDS_ITEM_KEY_PREFIX}${d.id}`,
      }))
    : [
        {
          label: isLoading ? 'Loading…' : 'No dashboards to display',
          key: `${DASHBOARDS_ITEM_KEY_PREFIX}empty`,
          disabled: true,
        },
      ];
}

export function getDashboardNavMain({
  dashboards,
  isLoading,
}: {
  dashboards: DashboardData[];
  isLoading: boolean;
}): Required<MenuProps>['items'][number] {
  return {
    label: <DashboardNavMainLabel />,
    key: DASHBOARDS_ITEM_KEY,
    icon: <DashboardOutlined />,
    children: getDashboardNavItems({ dashboards, isLoading }),
  };
}
